import { useMemo } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { HandshakeIcon, UsersIcon } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@ems/ui/components/alert'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'
import { formatDateTime } from '@ems/ui/lib/format'

import {
  gridState,
  locationOf,
  meetingTone,
  participation,
  type Meeting,
  type SpeakerProfile,
} from '#admin/features/program/api.ts'
import { gridQuery, meetingsQuery, speakerProfilesQuery } from '#admin/features/program/queries.ts'
import { label } from '#admin/features/registrations/api.ts'
import { MeetingsSkeleton } from '#admin/features/program/program-skeletons.tsx'

/**
 * The meeting desk: the grid, who is taking meetings, and the queue.
 *
 * **Everything on this page is `business-matching`**, which is exactly what the navigation gates
 * it on. The agenda (`/program/sessions`) is gated on `rundown`, and the venue (`/program/rooms`)
 * on `room`. This split stops a 404 in a loader for a tenant who bought one and not the other.
 *
 * Three things the page has to make obvious, and each is an invariant wearing interface clothes:
 *
 * **Whether the grid is bookable at all.** Draft-then-publish is one word on a row, so an
 * operator with a built-but-unpublished grid sees no requests and no reason why.
 *
 * **Where each accepted meeting is.** A location is allocated at acceptance and never before, so
 * a pending row genuinely has nowhere — and shows nothing rather than a "TBC" that would suggest
 * the desk has already done something.
 *
 * **Who is actually in the room.** The attendee list always includes the requester, resolved
 * from the meeting rather than stored (invariant 09), so "2 attendees" means the delegate plus
 * one colleague — never a count that silently omits the person whose meeting it is.
 */
export const Route = createFileRoute('/_authenticated/program/meetings')({
  pendingComponent: MeetingsSkeleton,
  staticData: { crumb: { parents: [{ label: 'Program' }], label: 'Meetings' } },
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(meetingsQuery()),
      context.queryClient.ensureQueryData(gridQuery()),
      context.queryClient.ensureQueryData(speakerProfilesQuery()),
    ]),
  component: MeetingsPage,
})

const GRID_MESSAGE: Record<ReturnType<typeof gridState>, string> = {
  empty: 'No slot grid yet — nothing can be booked.',
  draft: 'The grid is a draft. Nothing is bookable until it is published.',
  published: 'The grid is published and open for requests.',
  mixed: 'Part of the grid is published; the rest is still a draft and is not bookable.',
}

const ORDER: Meeting['status'][] = ['pending', 'accepted', 'no_show', 'rejected', 'canceled']

function MeetingsPage() {
  const { data } = useSuspenseQuery(meetingsQuery())
  const { data: grid } = useSuspenseQuery(gridQuery())
  const { data: speakers } = useSuspenseQuery(speakerProfilesQuery())

  const state = gridState(grid.items)
  const counts = new Map<Meeting['status'], number>()
  for (const meeting of data.items) {
    counts.set(meeting.status, (counts.get(meeting.status) ?? 0) + 1)
  }

  // Pending first, because pending is the only status anybody has to act on.
  const sorted = [...data.items].toSorted(
    (a, b) =>
      ORDER.indexOf(a.status) - ORDER.indexOf(b.status) ||
      Date.parse(a.startsAt) - Date.parse(b.startsAt),
  )

  const meetingColumns = useMemo<ColumnDef<Meeting, never>[]>(
    () => [
      {
        id: 'when',
        header: 'When',
        meta: { primary: true },
        cell: ({ row }) => (
          <span className="font-mono text-xs">{formatDateTime(row.original.startsAt)}</span>
        ),
      },
      { accessorKey: 'speakerName', header: 'Speaker' },
      {
        id: 'attendees',
        header: 'In the room',
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.attendees.map((a) => a.name).join(', ')}
          </span>
        ),
      },
      {
        id: 'where',
        header: 'Where',
        // Nothing, not "TBC": a pending meeting has no room because none has been taken, and
        // acceptance is what takes one.
        cell: ({ row }) =>
          locationOf(row.original) ?? <span className="text-muted-foreground">—</span>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge tone={meetingTone(row.original.status)}>
            {label(row.original.status)}
          </StatusBadge>
        ),
      },
    ],
    [],
  )

  const speakerColumns = useMemo<ColumnDef<SpeakerProfile, never>[]>(
    () => [
      { accessorKey: 'name', header: 'Speaker', meta: { primary: true } },
      {
        id: 'company',
        header: 'Company',
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.company ?? '—'}</span>
        ),
      },
      {
        id: 'marketplace',
        header: 'Marketplace',
        // Three states, in words. "Open (not yet confirmed)" is a speaker who never answered and
        // is bookable anyway — the null that must not read as "off".
        cell: ({ row }) => participation(row.original).label,
      },
      {
        id: 'slots',
        header: 'Slots open',
        meta: { align: 'end' },
        cell: ({ row }) => row.original.availableSlotIds.length,
      },
    ],
    [],
  )

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Meetings"
        description={
          ORDER.filter((status) => (counts.get(status) ?? 0) > 0)
            .map((status) => `${counts.get(status) ?? 0} ${label(status)}`)
            .join(' · ') || 'No meetings yet'
        }
      />

      {/*
        In words rather than as a badge color: the consequence — nobody can book — is not
        something an operator should have to infer from a tint. An `Alert` rather than a bordered
        paragraph so the published case reads as ordinary and the rest reads as something to fix.
      */}
      <Alert variant={state === 'published' ? 'default' : 'destructive'}>
        <AlertTitle>{state === 'published' ? 'Bookable' : 'Not bookable'}</AlertTitle>
        <AlertDescription>{GRID_MESSAGE[state]}</AlertDescription>
      </Alert>

      <DataTable
        caption="Meeting requests"
        title="Requests"
        description="Pending first, because pending is the only status anybody has to act on."
        columns={meetingColumns}
        data={sorted}
        getRowId={(meeting) => meeting.id}
        empty={
          <EmptyState
            icon={<HandshakeIcon />}
            title="No meetings have been requested yet"
            description="Delegates can request a meeting once the slot grid is published and a speaker has opened times."
          />
        }
      />

      <DataTable
        caption="Speakers taking meetings"
        title="Speakers"
        description="Who is taking meetings, and how much of their grid is still open."
        columns={speakerColumns}
        data={speakers.items}
        getRowId={(speaker) => speaker.speakerId}
        empty={
          <EmptyState
            icon={<UsersIcon />}
            title="No published speakers yet"
            description="A speaker has to be published before a delegate can ask them for a meeting."
          />
        }
      />
    </div>
  )
}
